import React, {useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {StatusIcon, AudioIcon, ChatIcon, ChatIconAdmin, RatingIconDown, RatingIconUp, MessageStatus} from "./icons";
import {Config} from "../../config";
import {rateMessage, selectNegativeMessageCountUpTo} from "../../slice/chat.slice";
import {LiveChatToggler} from "./live.chat.toggler";

const nl2br = (text) => {
    if (!text) return '';
    // Escape HTML entities first to prevent XSS
    const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    // Replace exactly 2 consecutive newlines with one <br>, then remaining single newlines with <br>
    return escaped.replace(/\n{2}/g, '<br>').replace(/\n/g, '<br>');
};

// Custom link component - same-domain links open in same tab, external links in new tab
const MarkdownLink = ({href, children}) => {
    let isSameDomain = false;
    try {
        const linkHost = new URL(href, window.location.origin).hostname;
        isSameDomain = linkHost === window.location.hostname;
    } catch (e) {}

    return isSameDomain
        ? <a href={href}>{children}</a>
        : <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
};

const MarkdownComponents = {
    a: MarkdownLink,
    table: ({children}) => <div className="sb-chat--table-wrapper"><table>{children}</table></div>,
};

const StatusTagRegex = /(<(?:success|error|details)>[\s\S]*?<\/(?:success|error|details)>)/g;

const RowRegex = /<row>\s*<title>([\s\S]*?)<\/title>\s*<value>([\s\S]*?)<\/value>\s*<\/row>/g;

const DetailsContent = ({content, useMarkdown}) => {
    const rows = [];
    let match;
    while ((match = RowRegex.exec(content)) !== null) {
        rows.push({title: match[1].trim(), value: match[2].trim()});
    }
    RowRegex.lastIndex = 0;
    if (rows.length === 0) return <span>{content}</span>;
    return (
        <div className="sb-details-rows">
            {rows.map((row, i) => (
                <div key={i} className="sb-details-row">
                    <span className="sb-details-row--title">{row.title}</span>
                    <span className="sb-details-row--value">{row.value}</span>
                </div>
            ))}
        </div>
    );
};

const getStatus = (text) => {
    if (/<error>[\s\S]*?<\/error>/.test(text)) return 'error';
    if (/<success>[\s\S]*?<\/success>/.test(text)) return 'success';
    return null;
};

const getStatusClass = (text) => {
    const status = getStatus(text);
    return status ? ` text__${status}` : '';
};

const MessageContent = ({text, useMarkdown}) => {
    if (!text) return null;

    StatusTagRegex.lastIndex = 0;
    if (StatusTagRegex.test(text)) {
        const parts = text.split(StatusTagRegex);
        return (
            <div className={`text ${getStatusClass(text)}`}>
                <MessageStatus status={getStatus(text)} />
                {parts.map((part, i) => {
                    const tagMatch = part.match(/^<(success|error|details)>([\s\S]*?)<\/\1>$/);
                    if (tagMatch) {
                        if (tagMatch[1] === 'details') {
                            return <span key={i} className="sb-message-status-tag sb-message-status-tag--details"><DetailsContent content={tagMatch[2]} useMarkdown={useMarkdown} /></span>;
                        }
                        if (useMarkdown) {
                            return <span key={i} className={`sb-message-status-tag sb-message-status-tag--${tagMatch[1]}`}><ReactMarkdown remarkPlugins={[remarkGfm]} components={MarkdownComponents}>{tagMatch[2]}</ReactMarkdown></span>;
                        }
                        return <span key={i} className={`sb-message-status-tag sb-message-status-tag--${tagMatch[1]}`}>{tagMatch[2]}</span>;
                    }
                    if (!part) return null;
                    if (useMarkdown) {
                        return (
                            <ReactMarkdown key={i} remarkPlugins={[remarkGfm]} components={MarkdownComponents}>
                                {part}
                            </ReactMarkdown>
                        );
                    }
                    return <span key={i} dangerouslySetInnerHTML={{__html: nl2br(part)}} />;
                })}
            </div>
        );
    }

    if (useMarkdown) {
        return (
            <div className={`text ${getStatusClass(text)}`}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MarkdownComponents}>
                    {text}
                </ReactMarkdown>
            </div>
        );
    }

    return (
        <div className={`text ${getStatusClass(text)}`} dangerouslySetInnerHTML={{__html: nl2br(text)}} />
    );
};

// Walk up from the bottom anchor to the nearest scrollable ancestor. The chat
// scrolls on .sb-chat--content in the widget but on .sb-chat--page-layout in the
// admin full-page view, so detect it dynamically instead of hard-coding it.
const getScrollParent = (el) => {
    let node = el ? el.parentElement : null;
    while (node) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
};

export default function MessageLog() {
    const messages = useSelector((state) => state.chat.messages);
    const chatRatingNegativeSinceIndex = useSelector((state) => state.chat.chatRatingNegativeSinceIndex);
    const dispatch = useDispatch();
    const endRef = useRef(null);
    const config = Config.getInstance();
    const supportMarkdown = config.support_md || false;


    const hashChangeHandler = () => {
        endRef.current.scrollIntoView({behavior: "instant"});
    }

    // Autoscroll keeps newly arriving content in view. It is keyed on the
    // message count and the LAST message's text only, so mutations on older
    // messages (rating, dbId backfill) never scroll the view — e.g. rating an
    // old message must not yank to the bottom.
    const messagesCount = messages.length;
    const lastMessageText = messages.length > 0 ? (messages[messages.length - 1].text || '') : '';
    const rafRef = useRef(null);
    const didInitialScrollRef = useRef(false);
    useEffect(() => {
        const scroller = getScrollParent(endRef.current);
        if (!scroller) return;

        // First positioning (mount / first messages): jump to the bottom with
        // no animation.
        if (!didInitialScrollRef.current) {
            didInitialScrollRef.current = true;
            scroller.scrollTop = scroller.scrollHeight;
            return;
        }

        // Afterwards glide: a single requestAnimationFrame loop eases scrollTop
        // toward the bottom, recomputing the target every frame. This follows
        // streaming growth continuously (smooth) instead of snapping per token
        // (steppy) or restarting a browser smooth-scroll per token (jitter).
        cancelAnimationFrame(rafRef.current);
        const step = () => {
            const goal = scroller.scrollHeight - scroller.clientHeight;
            const distance = goal - scroller.scrollTop;
            if (distance <= 1) {
                scroller.scrollTop = goal;
                rafRef.current = null;
                return;
            }
            scroller.scrollTop += Math.max(1, distance * 0.15);
            rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);

        return () => cancelAnimationFrame(rafRef.current);
    }, [messagesCount, lastMessageText]);

    useEffect(() => {
        window.addEventListener("hashchange", hashChangeHandler);
        return () => {
            window.removeEventListener("hashchange", hashChangeHandler);
        };
    }, []);

    return (
        <div className={"sb-chat--content " + (messages.length === 0 ? "sb-chat--content_empty" : "")}>
            <div className="sb-chat--greeting">
                <MeaasgeGreeting isAdmin={config.is_admin} />
            </div>
            <div className="sb-chat--content_conversation">
                {messages.map((message, index) => (
                    <div
                        key={message.id}
                        className={`sb-message sb-message-${message.sender} sb-status-message-${message.status} sb-message-type-${message.type}`}
                    >
                        {config.is_admin && message.sender === 'bot' && (
                            <div className={'sb-message_sender'}>
                                <ChatIconAdmin />
                                <div className={'sb-message_nickname'}>Simply AI Assistant</div>
                            </div>
                        )}


                        {message.type === "audio" && <AudioIcon />}
                        {message.text?.length > 0 && (
                            <MessageContent text={message.text} useMarkdown={supportMarkdown} />
                        )}
                        {message.payment_link && <PaymentButton link={message.payment_link} />}
                        {message.call_support && <LiveChatToggler />}
                        {message.status && <StatusIcon status={message.status} />}
                        {message.sender === 'bot' && message.status === 'done' && message.dbId && (
                            <MessageRating
                                dbId={message.dbId}
                                currentRating={message.rating}
                                onRate={(rating) => dispatch(rateMessage({ dbId: message.dbId, rating }))}
                            />
                        )}
                        {message.sender === 'bot' && message.status === 'done' && (
                            (chatRatingNegativeSinceIndex !== null && index >= chatRatingNegativeSinceIndex) ||
                            selectNegativeMessageCountUpTo(messages, index) >= 3
                        ) && (
                            <LiveChatToggler />
                        )}
                    </div>
                ))}
            </div>
            <div ref={endRef}/>
        </div>
    );
}

const MeaasgeGreeting = ({isAdmin}) => (
    isAdmin ?
        <React.Fragment>
            <div className={'sb-chat--alert'}>
                This AI assistant is powered by SimplyBook.me Ltd and OpenAI. Your inputs are processed solely to operate this feature and are never used to train AI models. See our <a href='https://simplybook.me/en/policy' target='_blank'>Privacy Policy</a> for more. Always verify important information.
            </div>

            <span className={'sb-chat--greeting_icon'}>
                <ChatIconAdmin />
            </span>

            <h3 className={'sb-chat--greeting_title bold title title--h3 '}>Hi there!</h3>
            <div className={'sb-chat--greeting_subtitle p p--primary'}><b>How can I help you today?</b></div>
            <div className={'p p--secondary'}>I can help you manage bookings, create services, or analyze your dashboard data.</div>
        </React.Fragment>
        :
        <React.Fragment>
            <span className={'sb-chat--greeting_icon'}>
                <ChatIcon />
            </span>
            <h3 className={'sb-chat--greeting_title'}>Hello and welcome! &#128075;</h3>
            <div className={'sb-chat--greeting_txt'}>I am your AI assistant!</div>
            <div className={'sb-chat--greeting_txt'}>To get you booked, I'll need a few details.</div>
            <div className={'sb-chat--greeting_txt'}>(Just a note: We process your info via <a href='https://simplybook.me/en/' target='_blank'>SimplyBook.me</a>  and OpenAI to manage this appointment. Your data is private and never used to train AI models. <a href='https://simplybook.me/en/policy' target='_blank'>Privacy Policy</a>)</div>
            <div className={'sb-chat--greeting_txt'}>First, what service are you looking to book today?</div>
        </React.Fragment>
)

const PaymentButton = ({link}) => (
    <a href={link} className={"sb-payment-link"}>Pay now</a>
)

const MessageRating = ({dbId, currentRating, onRate}) => (
    <div className="sb-message-rating">
        <button
            className={"sb-message-rating--btn" + (currentRating === 1 ? " sb-message-rating--btn_active" : "")}
            type="button"
            title="Helpful"
            disabled={!!currentRating}
            onClick={() => onRate(1)}
        ><RatingIconUp /></button>
        <button
            className={"sb-message-rating--btn" + (currentRating === -1 ? " sb-message-rating--btn_active" : "")}
            type="button"
            title="Not helpful"
            disabled={!!currentRating}
            onClick={() => onRate(-1)}
        ><RatingIconDown /></button>
    </div>
)